import React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * C3: the company / period switcher at the top of the sidebar. A dark row in the menu band (a small label, the value,
 * a chevron) that opens a popover listbox: no white boxes in the band.
 *
 * The popover is a listbox (role="listbox", options with aria-selected), driven from the keyboard like a select:
 * Enter, Space or ArrowDown on the trigger open it; ArrowUp/ArrowDown, Home/End move; Enter picks; Esc or Tab close
 * it, and focus goes back to the trigger. With `filterable` it has a search box first (type to filter; the arrows
 * still move through the list, via aria-activedescendant). The selected option has a tick. Collapsed to the icon
 * rail, the trigger is just the icon (named for screen readers) and the popover opens to its right.
 */
export interface PickerOption {
  id: string;
  label: string;
  /** Shown after the label, muted (e.g. a period's status). */
  extra?: React.ReactNode;
}

export interface ContextPickerProps {
  /** COMPANY, PERIOD: the row's small label and the control's accessible name. */
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: React.ReactNode;
  /** The value as plain text: the collapsed icon's tooltip and accessible name. */
  valueText: string;
  options: PickerOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  filterable?: boolean;
  collapsed?: boolean;
  disabled?: boolean;
}

const focusRing = 'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mp-accent';

const ContextPicker: React.FC<ContextPickerProps> = (
  { label, icon: Icon, value, valueText, options, selectedId, onSelect, filterable = false, collapsed = false, disabled = false },
) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const pop = React.useRef<HTMLDivElement>(null);
  const filterRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const id = React.useId();
  const listId = `${id}-list`;

  const shown = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const openIt = () => {
    if (disabled) return;
    const r = trigger.current!.getBoundingClientRect();
    const width = Math.min(Math.max(r.width, 320), window.innerWidth - 16);      // wide enough for a full company name
    setPos(collapsed
      ? { top: Math.max(8, Math.min(r.top, window.innerHeight - 360)), left: r.right + 8, width }
      : { top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width });
    setQuery('');
    setActive(Math.max(0, options.findIndex(o => o.id === selectedId)));
    setOpen(true);
  };
  const close = (refocus = true) => { setOpen(false); if (refocus) trigger.current?.focus(); };
  const pick = (o: PickerOption | undefined) => { if (!o) return; onSelect(o.id); close(); };

  // focus the search box (or the list) when it opens; keep the active option in view
  React.useEffect(() => { if (open) (filterable ? filterRef.current : listRef.current)?.focus(); }, [open, filterable]);
  React.useEffect(() => { setActive(a => Math.min(a, Math.max(shown.length - 1, 0))); }, [shown.length]);
  React.useEffect(() => {
    if (open) document.getElementById(`${id}-opt-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, id]);
  // a click outside closes it
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !trigger.current?.contains(t)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIt(); }
  };
  const onPopKey = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setActive(a => Math.min(a + 1, shown.length - 1)); break;
      case 'ArrowUp': e.preventDefault(); setActive(a => Math.max(a - 1, 0)); break;
      case 'Home': if (!filterable) { e.preventDefault(); setActive(0); } break;
      case 'End': if (!filterable) { e.preventDefault(); setActive(shown.length - 1); } break;
      case 'Enter': e.preventDefault(); pick(shown[active]); break;
      case ' ': if (!filterable) { e.preventDefault(); pick(shown[active]); } break;
      case 'Escape': e.preventDefault(); close(); break;
      case 'Tab': close(false); break;
    }
  };

  const activeId = shown.length ? `${id}-opt-${active}` : undefined;

  return (
    <>
      <button ref={trigger} type="button" disabled={disabled} onClick={() => (open ? close() : openIt())} onKeyDown={onTriggerKey}
              aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
              aria-label={collapsed ? `${label}: ${valueText}` : undefined} title={collapsed ? `${label}: ${valueText}` : undefined}
              className={cn('group flex w-full items-center rounded-md text-left transition-colors disabled:opacity-60', focusRing,
                            collapsed ? 'justify-center p-2 text-mp-nav-text hover:bg-mp-nav-active-bg hover:text-mp-nav-active-text'
                              : 'h-mp-nav-item gap-2 border border-mp-nav-border bg-transparent px-mp-nav-x hover:bg-mp-nav-active-bg',
                            open && !collapsed && 'bg-mp-nav-active-bg')}>
        {collapsed ? <Icon className="h-5 w-5" /> : (
          <>
            {/* one line (nav compact): the icon says which picker; its name stays for assistive tech */}
            <Icon className="h-4 w-4 shrink-0 text-mp-nav-text" aria-hidden />
            <span className="sr-only">{label}: </span>
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-mp-nav-size font-medium text-mp-nav-active-text"
                  title={`${label}: ${valueText}`}>{value}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-mp-nav-text group-hover:text-mp-nav-active-text" aria-hidden />
          </>
        )}
      </button>
      {open && pos && (
        <div ref={pop} onKeyDown={onPopKey} style={{ top: pos.top, left: pos.left, width: pos.width }}
             className="fixed z-[100] overflow-hidden rounded-md border border-mp-border bg-mp-surface font-mp-sans text-mp-text shadow-lg"
             data-testid={`picker-${label.toLowerCase()}`}>
          {filterable && (
            <div className="relative border-b border-mp-border p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-mp-text-muted" aria-hidden />
              <input ref={filterRef} value={query} onChange={e => { setQuery(e.target.value); setActive(0); }}
                     placeholder={`Find a ${label.toLowerCase()}`} aria-label={`Find a ${label.toLowerCase()}`}
                     role="combobox" aria-expanded aria-controls={listId} aria-activedescendant={activeId} aria-autocomplete="list"
                     className="w-full rounded-md border border-mp-border bg-mp-surface py-1.5 pl-8 pr-2 text-mp-nav-size focus:border-mp-accent focus:outline-none" />
            </div>
          )}
          <ul ref={listRef} id={listId} role="listbox" aria-label={label} tabIndex={filterable ? -1 : 0}
              aria-activedescendant={filterable ? undefined : activeId}
              className="max-h-72 overflow-y-auto py-1 focus:outline-none">
            {shown.length === 0 && <li className="px-3 py-2 text-mp-nav-size text-mp-text-muted" role="presentation">No {label.toLowerCase()} matches “{query}”</li>}
            {shown.map((o, i) => {
              const sel = o.id === selectedId;
              return (
                <li key={o.id} id={`${id}-opt-${i}`} role="option" aria-selected={sel}
                    onMouseEnter={() => setActive(i)} onMouseDown={e => e.preventDefault()} onClick={() => pick(o)}
                    className={cn('flex cursor-pointer items-center gap-2 px-3 py-2 text-mp-nav-size',
                                  i === active && 'bg-mp-surface-2', sel && 'font-medium')}>
                  <Check className={cn('h-4 w-4 shrink-0 text-mp-accent', !sel && 'invisible')} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.extra && <span className="shrink-0 text-mp-sm text-mp-text-muted">{o.extra}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default ContextPicker;
